import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingVastuAccess, setIsCheckingVastuAccess] = useState(false);
  const [error, setError] = useState(null);
  const [enrollments, setEnrollments] = useState([]); 
  const [hasVastuConsultantAccess, setHasVastuConsultantAccess] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyToken();
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleSessionCleared = () => {
      setUser(null);
      setError(null);
      setEnrollments([]);
      setHasVastuConsultantAccess(false);
      setIsLoading(false);
    };

    window.addEventListener('auth:session-cleared', handleSessionCleared);
    return () => window.removeEventListener('auth:session-cleared', handleSessionCleared);
  }, []);

  const fetchEnrollments = async () => {
    try {
      // Assuming apiService.getMyEnrollments() exists and returns { success: true, data: [...] }
      const response = await apiService.getMyEnrollments();
      if (response.success) {
        setEnrollments(response.data);
      }
    } catch (err) {
      // Silently fail, as this is a secondary data fetch
      console.error("Failed to fetch user enrollments:", err);
    }
  };

  const verifyToken = async () => {
    try {
      const response = await apiService.getCurrentUser();
      if (response.success) {
        setUser(response.data.user);
        await syncVastuConsultantAccess(response.data.user);
        await fetchEnrollments(); // Fetch enrollments after getting user
      } else {
        apiService.clearAuthData();
        setUser(null);
        setEnrollments([]);
        setHasVastuConsultantAccess(false);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      apiService.clearAuthData();
      setUser(null);
      setEnrollments([]);
      setHasVastuConsultantAccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const syncVastuConsultantAccess = async (userData) => {
    if (!userData) {
      setHasVastuConsultantAccess(false);
      return false;
    }

    if (['admin', 'consultant', 'vastu_consultant'].includes(userData.role)) {
      setHasVastuConsultantAccess(userData.role !== 'admin');
      return userData.role !== 'admin';
    }

    setIsCheckingVastuAccess(true);
    try {
      const response = await apiService.checkVastuConsultantAccess();
      const hasAccess = !!response.data?.hasAccess;
      setHasVastuConsultantAccess(hasAccess);
      return hasAccess;
    } catch (error) {
      setHasVastuConsultantAccess(false);
      return false;
    } finally {
      setIsCheckingVastuAccess(false);
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      const response = await apiService.login(credentials);

      if (response.success) {
        const { user: userData, token } = response.data;
        localStorage.setItem('token', token);
        setUser(userData);
        const hasVastuAccess = await syncVastuConsultantAccess(userData);
        await fetchEnrollments(); // Fetch enrollments after login
        return { success: true, user: userData, hasVastuConsultantAccess: hasVastuAccess };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // --- NEW UNIFIED OTP METHODS ---

  const requestOtp = async (identifier) => {
    try {
      setError(null);
      const response = await apiService.requestLoginOtp(identifier);
      return { 
        success: response.success, 
        message: response.message, 
        data: response.data 
      };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  const verifyOtp = async (identifier, otp) => {
    try {
      setError(null);
      const response = await apiService.verifyLoginOtp(identifier, otp);

      if (response.success) {
        const { user: userData, token } = response.data;
        localStorage.setItem('token', token);
        setUser(userData);
        const hasVastuAccess = await syncVastuConsultantAccess(userData);
        await fetchEnrollments(); // Sync enrollments on successful login
        return { success: true, user: userData, hasVastuConsultantAccess: hasVastuAccess };
      }

      throw new Error(response.message || 'OTP login failed');
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await apiService.register(userData);

      if (response.success) {
        const { user: newUser, token } = response.data;
        localStorage.setItem('token', token);
        setUser(newUser);
        await syncVastuConsultantAccess(newUser);
        // Enrollments are fetched on page reload/token verification, no need to fetch here
        return { success: true, user: newUser };
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      apiService.clearAuthData();
      setUser(null);
      setError(null);
      setEnrollments([]); // Clear enrollments on logout
      setHasVastuConsultantAccess(false);
    }
  };

  const updateUser = async (updatedData) => {
    try {
      setError(null);
      const response = await apiService.updateProfile(updatedData);

      if (response.success) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      } else {
        throw new Error(response.message || 'Profile update failed');
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  const clearError = () => {
    setError(null);
  };

  const loginWithGoogle = async (credential) => {
    try {
      setError(null);
      const response = await apiService.googleLogin({ credential });

      if (response.success) {
        const { user: userData, token } = response.data;
        localStorage.setItem('token', token);
        setUser(userData);
        const hasVastuAccess = await syncVastuConsultantAccess(userData);
        await fetchEnrollments(); // Fetch enrollments after Google login
        return { success: true, user: userData, hasVastuConsultantAccess: hasVastuAccess };
      } else {
        throw new Error(response.message || 'Google login failed');
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    isLoggedIn: !!user && !!localStorage.getItem('token'),
    isLoading,
    isCheckingVastuAccess,
    error,
    enrollments, 
    hasVastuConsultantAccess,
    fetchEnrollments, 
    login,
    requestOtp,  // Expose the new methods
    verifyOtp,    // Expose the new methods
    register,
    logout,
    updateUser,
    clearError,
    loginWithGoogle,
    verifyToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
